import type { BulkActionDescriptor } from '@jms/model';
import type { Command } from '@workbuddy/ui/model';
import { resolveCommandSemanticId, type SemanticCommandId } from '@workbuddy/ui/services/keyboard.shortcuts';
import type { EntityBoardBulkCommandSettingsPanel } from './context/entity.board.commands';

export type BulkPaletteFieldPanel = Exclude<EntityBoardBulkCommandSettingsPanel, { kind: 'action' } | { kind: 'conditional' }>;

export interface BulkPaletteEntry {
  commandId: string;
  config: EntityBoardBulkCommandSettingsPanel | null;
}

export interface BulkPaletteSelection {
  commandId: string;
  config?: BulkPaletteFieldPanel;
  value?: unknown;
}

export function hasBulkPanelValue(config: BulkPaletteFieldPanel, value: unknown): boolean {

  if (config.kind === 'input' || config.kind === 'dateInput') return typeof value === 'string' && value.trim().length > 0;

  return config.mode === 'multi' ? Array.isArray(value) && value.length > 0 : value !== '' && value !== null && value !== undefined;
}

/**
 * The stamp names the row the user actually activated, so it wins outright. Scanning sibling panels
 * first meant a stage left in the Move picker was dispatched when the user clicked Delete.
 */
export function resolveBulkPaletteSelection(entries: BulkPaletteEntry[], stamp: string, readValue: (fieldId: string) => unknown): BulkPaletteSelection | null {

  const stamped = stamp ? entries.find(entry => entry.commandId === stamp) : undefined;

  if (!stamped?.config) return null;
  if (stamped.config.kind === 'action') return { commandId: stamped.commandId };
  if (stamped.config.kind === 'conditional') return null;

  const value = readValue(stamped.config.fieldId);
  return hasBulkPanelValue(stamped.config, value) ? { commandId: stamped.commandId, config: stamped.config, value } : null;
}

export type BulkShortcutTarget = { kind: 'bulk-action'; command: Command } | { kind: 'palette'; command: Command };

/**
 * A field command has no value until the user picks one, so its chord opens the palette deep-linked
 * into that command's value panel instead of dispatching an empty request. The target carries the
 * command so the host can resolve the panel. Only an action-kind command runs outright.
 */
export function resolveBulkShortcutTarget(semanticId: SemanticCommandId, commands: readonly Command[]): BulkShortcutTarget | null {

  const command = commands.find(candidate => resolveCommandSemanticId(candidate._id, resolveBulkActionDescriptor(candidate)) === semanticId);
  if (!command) return null;

  return bulkCommandOpensPalette(command) ? { kind: 'palette', command } : { kind: 'bulk-action', command };
}

/** Bar / chord: a value panel must open before execute. Action-kind and panel-less commands run now. */
export function bulkCommandOpensPalette(command: Command): boolean {

  const kind = command.data?.commandPalette?.settingsPanel?.kind;
  return !!kind && kind !== 'action';
}

function resolveBulkActionDescriptor(command: Command): BulkActionDescriptor | undefined {

  return (command.data as { bulkActionDescriptor?: BulkActionDescriptor } | undefined)?.bulkActionDescriptor;
}
